import { darkSurfaceTokens } from "./surfaceTokens";
import { alpha, createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { GridNoResultsOverlay } from "@components/feedback/GridEmptyOverlay";

export function createAppTheme(mode: "light" | "dark" = "light") {
  const dark = mode === "dark";
  const line = dark ? "rgba(195,225,244,.42)" : "#E2E8F2";
  const paper = dark ? "rgba(12,24,32,.48)" : "#FFFFFF";
  const glass = dark
    ? {
        backgroundImage:
          "linear-gradient(135deg, rgba(218,239,250,.14), rgba(150,199,221,.025) 45%, rgba(185,227,243,.07))",
        backdropFilter: "blur(22px) saturate(145%)",
        WebkitBackdropFilter: "blur(22px) saturate(145%)",
      }
    : {};
  return createTheme({
    palette: {
      mode,
      primary: dark
        ? {
            main: "#64B5FF",
            dark: "#3395ED",
            light: "#15314B",
            contrastText: "#071522",
          }
        : { main: "#006DCC", dark: "#004A99", light: "#E9F4FF" },
      background: {
        default: dark ? "#08121c" : "#F5F7FB",
        paper: dark ? "#101d28" : "#FFFFFF",
      },
      text: {
        primary: dark ? "#EDF4FB" : "#0B1739",
        secondary: dark ? "#AABCCD" : "#60708F",
      },
      divider: line,
      success: { main: dark ? "#5CE99C" : "#14804A" },
      warning: { main: dark ? "#F5C568" : "#A15C00" },
      error: { main: dark ? "#FF8D98" : "#C62828" },
    },
    shape: { borderRadius: dark ? 12 : 8 },
    typography: {
      fontFamily: 'Inter, Roboto, "Helvetica Neue", Arial, sans-serif',
      body1: { fontSize: "0.875rem" },
      body2: { fontSize: "0.8125rem" },
      h1: {
        fontSize: "clamp(1.35rem, 2vw, 1.65rem)",
        fontWeight: 800,
        letterSpacing: "-0.03em",
      },
      h2: { fontSize: "1rem", fontWeight: 800 },
      h3: { fontSize: "0.95rem", fontWeight: 750 },
      button: { fontWeight: 700, textTransform: "none" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": dark
            ? darkSurfaceTokens
            : Object.fromEntries(
                Object.keys(darkSurfaceTokens).map((key) => [key, "initial"]),
              ),
          body: {
            minWidth: 320,
            backgroundImage: dark
              ? 'linear-gradient(180deg, rgba(5,13,21,.32), rgba(5,13,21,.68) 90%), url("/branding/orbit-glass-dubai.jpg")'
              : "none",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundAttachment: "fixed",
          },
          "@supports not (backdrop-filter: blur(1px))": {
            ".MuiPaper-root": { backgroundColor: dark ? "#101d28" : "#FFFFFF" },
          },
          "@media (prefers-reduced-transparency: reduce)": {
            ".MuiPaper-root": {
              backdropFilter: "none",
              backgroundColor: dark ? "#101d28" : "#FFFFFF",
            },
          },
          "@media print": {
            body: {
              backgroundImage: "none",
              backgroundColor: "white",
              color: "black",
            },
            ".MuiPaper-root": {
              background: "white",
              color: "black",
              backdropFilter: "none",
              boxShadow: "none",
            },
          },
          a: { color: "inherit", textDecoration: "none" },
          "*:focus-visible": {
            outline: dark
              ? "3px solid #64B5FF"
              : "3px solid rgba(0,109,204,.35)",
            outlineOffset: 2,
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundColor: paper, ...glass } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: dark ? "rgba(5,17,29,.72)" : "#FFFFFF",
            "& fieldset": { borderColor: line },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { color: dark ? "#AABCCD" : undefined } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: line },
          head: {
            backgroundColor: dark ? "#142634" : "#F8FAFD",
            color: dark ? "#DCE8F3" : undefined,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: dark ? "rgba(16,29,40,.98)" : "#FFFFFF",
            border: `1px solid ${line}`,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { backgroundColor: dark ? "#142431" : "#FFFFFF" },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: dark ? "#244154" : undefined },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 7, minHeight: 34, fontSize: 13 },
          outlinedPrimary: dark
            ? {
                color: "#EDFFF3",
                borderColor: "rgba(162,245,184,.65)",
                background:
                  "linear-gradient(105deg, rgba(112,242,70,.35), rgba(23,80,47,.38) 30%, rgba(11,34,27,.5) 75%, rgba(135,211,173,.15))",
                boxShadow:
                  "inset 1px 0 4px rgba(174,255,145,.55), inset 0 1px 0 rgba(207,255,220,.25), 0 0 14px rgba(91,236,112,.14)",
                transition:
                  "box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease",
                "&:hover": {
                  borderColor: "#ABFFB9",
                  background:
                    "linear-gradient(105deg, rgba(133,255,78,.55), rgba(27,101,53,.5) 30%, rgba(13,48,33,.65) 75%, rgba(158,237,191,.24))",
                  boxShadow:
                    "inset 2px 0 6px rgba(191,255,159,.75), inset 0 1px 0 rgba(222,255,231,.45), 0 0 22px rgba(91,236,112,.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "scale(.97)" },
                "&.Mui-disabled": {
                  background: "rgba(124,152,142,.05)",
                  borderColor: "rgba(151,176,166,.18)",
                  boxShadow: "none",
                  color: "rgba(183,206,197,.38)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                  "&:hover, &:active": { transform: "none" },
                },
              }
            : {},
          containedPrimary: dark
            ? {
                color: "#FFFFFF",
                backgroundColor: "#246fba",
                "&:hover": { backgroundColor: "#1d609f" },
              }
            : {},
          contained: dark ? { boxShadow: "0 0 14px rgba(59,160,255,.12)" } : {},
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${line}`,
            boxShadow: dark
              ? "0 10px 30px rgba(0,0,0,.24), 0 0 18px rgba(128,211,239,.10), inset 0 1px 0 rgba(234,249,255,.48), inset 0 -1px 0 rgba(176,226,245,.16)"
              : `0 3px 12px ${alpha("#0B3B75", 0.05)}`,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 16, "&:last-child": { paddingBottom: 16 } },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small" },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 7,
              background: dark ? "rgba(5,17,29,.72)" : "#FFFFFF",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: 25, fontSize: 12, fontWeight: 700 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${line}`,
            backgroundColor: dark ? "rgba(16,29,40,.97)" : "#FFFFFF",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: "none", borderBottom: `1px solid ${line}` },
        },
      },
      MuiDrawer: {
        styleOverrides: { paper: { borderRight: 0 } },
      },
      MuiDataGrid: {
        defaultProps: { slots: { noResultsOverlay: GridNoResultsOverlay } },
        styleOverrides: {
          root: {
            border: 0,
            fontSize: 12.5,
            "--DataGrid-containerBackground": dark ? "#142634" : "#FBFCFE",
            "--DataGrid-pinnedBackground": dark ? "#142634" : "#FFFFFF",
          },
          columnHeaders: {
            background: dark ? "#142634" : "#FBFCFE",
            borderBottom: `1px solid ${line}`,
          },
          columnHeaderTitle: { fontSize: 12, fontWeight: 800 },
          cell: {
            borderColor: line,
            paddingLeft: 12,
            paddingRight: 12,
          },
          row: {
            "&:hover": {
              backgroundColor: dark ? "rgba(100,181,255,.08)" : "#F8FAFD",
            },
          },
          footerContainer: { borderColor: line },
        },
      },
    },
  });
}

// Kept for isolated component previews and existing tests.
export const appTheme = createAppTheme("light");
